import { useState } from "react";
import { Link } from "@tanstack/react-router";
import {
  Alert,
  Badge,
  Button,
  Card,
  DataTable,
  Field,
  Input,
  PageHeader,
  Select,
  Spinner,
} from "@empite/ui-components";
import { isApiError } from "@empite/api-client";
import type {
  MarkupPlan,
  MarkupPlanEdit,
  NewMarkupPlan,
  NewPriceVersion,
  Operator,
  PriceVersion,
  PriceVersionTransition,
  Reason,
  RoundingMode,
  RoutingProfileRef,
} from "../admin-client";
import {
  useArchiveMarkupPlan,
  useCreateMarkupPlan,
  useCreatePriceVersion,
  useMarkupPlans,
  usePriceVersions,
  useSetPriceVersionStatus,
  useUpdateMarkupPlan,
} from "../admin-hooks";
import { ErrorAlert, RequireOperator, can, errorMessage } from "./guards";
import { ReasonButton } from "./reason-prompt";
import { StatusBadge, when } from "./common";

/** The permission the Admin API guards both writes on this page with;
 *  repeated here only to decide what to draw. */
const WRITE = "pricing:write";

/** One page of history per list. Pricing is read backwards from "what is in
 *  force" — nobody audits the two hundredth superseded version from here. */
const PAGE = 50;

const ROUNDING_MODES: RoundingMode[] = ["down", "up", "floor", "ceil", "half-up", "half-even"];

/**
 * The two ways a price version stops applying, and what each one means for
 * the gateway.
 *
 * `active` is deliberately absent: a closed version is never reopened, it is
 * replaced, because reopening one would put the rate a settled charge was
 * reconciled against back into force. The API's vocabulary has the same two
 * values for the same reason.
 *
 * The difference matters more than the words suggest, which is why both
 * sentences name the consequence rather than the transition: retiring is the
 * one that leaves a model with no price at all, and the gateway refuses a
 * request it cannot price.
 */
const PRICE_VERSION_ACTIONS: Array<{
  status: PriceVersionTransition;
  label: string;
  danger: boolean;
  description: string;
}> = [
  {
    status: "superseded",
    label: "Supersede",
    danger: false,
    description:
      "Closes this version because another one takes over the same pair from the same instant. It is refused if nothing does — closing the last open version is retiring it, and the API says so rather than leaving the model unpriced by accident.",
  },
  {
    status: "retired",
    label: "Retire",
    danger: true,
    description:
      "Closes this version with nothing behind it. From that moment the pair has no price, and the gateway refuses every request for it as unpriced. Anything already reserved still settles at the rate it was reserved under.",
  },
];

/**
 * The shape a rate has to be in before it is worth sending.
 *
 * A digits-and-one-dot check, nothing more: the server parses these with
 * internal/money and is the only thing entitled to decide what a rate means.
 * This exists so an operator who typed "3,00" or "$3.00" hears about it here
 * instead of as a 400 after the reason dialog.
 */
const DECIMAL = /^\d+(\.\d+)?$/;

function decimalError(value: string, required: boolean): string | undefined {
  const trimmed = value.trim();
  if (trimmed === "") {
    return required ? "Required." : undefined;
  }
  return DECIMAL.test(trimmed) ? undefined : "Digits and a decimal point only — no symbols.";
}

/** Optional money fields are sent only when they were typed. An empty string
 *  is not "zero", it is "the operator did not say", and the server's own
 *  default of "0" is the right answer to that. */
function optional(value: string): string | undefined {
  const trimmed = value.trim();
  return trimmed === "" ? undefined : trimmed;
}

/**
 * Pricing — what each model costs the platform, and what the platform charges
 * for it.
 *
 * Both halves are append-only histories rather than editable rows, which is
 * the one thing an operator has to understand before touching this page: see
 * the forms. Nothing on this screen edits a published number in place. The
 * writes it does carry are the two that are honest about that — closing a
 * version's window, and appending a successor.
 */
export function Pricing() {
  return (
    <RequireOperator>{(session) => <PricingPanels operator={session.operator} />}</RequireOperator>
  );
}

function PricingPanels({ operator }: { operator: Operator }) {
  const [notice, setNotice] = useState<string | null>(null);
  const writable = can(operator, WRITE);

  return (
    <>
      <PageHeader
        title="Pricing"
        description="Provider rates as they were bought, and the markup plans applied on top of them."
      />
      {notice ? <Alert tone="success" title={notice} /> : null}
      <PriceVersionsCard writable={writable} onDone={setNotice} />
      <MarkupPlansCard writable={writable} onDone={setNotice} />
    </>
  );
}

/** The effective window, as one cell. An open end is the version in force,
 *  and saying so beats an empty column. */
function EffectiveRange({ from, to }: { from: string; to?: string }) {
  return (
    <>
      <div>{when(from)}</div>
      <span className="esm-muted-text">{to ? `until ${when(to)}` : "still open"}</span>
    </>
  );
}

function PriceVersionsCard({
  writable,
  onDone,
}: {
  writable: boolean;
  onDone: (message: string | null) => void;
}) {
  const versions = usePriceVersions({ limit: PAGE });
  const setStatus = useSetPriceVersionStatus();

  return (
    <Card title="Provider price versions">
      {versions.isPending ? (
        <Spinner label="Loading price versions" />
      ) : versions.error ? (
        <ErrorAlert error={versions.error} title="Could not load the price versions" />
      ) : (
        // Every rate is printed exactly as the wire sent it. These are
        // decimal strings from internal/money; formatting them through a
        // Number would round "0.000015" into a price nobody agreed to.
        <>
          {/* Whatever the API said, verbatim. The refusal that matters here is
              the one for superseding a pair's only open version, and its
              sentence names the provider, the model and the currency it would
              have left unpriced — the whole of what the operator needs to
              decide whether they meant "retire". */}
          <ErrorAlert error={setStatus.error} title="That price version was not closed" />
          <DataTable<PriceVersion>
            rows={versions.data?.price_versions ?? []}
            rowKey={(row) => row.id}
            empty="No price has been published yet, so nothing can be costed."
            columns={[
              {
                key: "model",
                header: "Model",
                render: (row) => (
                  <>
                    <div>
                      <code>{row.model}</code>
                    </div>
                    <span className="esm-muted-text">{row.provider}</span>
                  </>
                ),
              },
              { key: "currency", header: "Currency", render: (row) => row.currency },
              {
                key: "input",
                header: "Input / M",
                align: "end",
                render: (row) => <code>{row.input_per_million}</code>,
              },
              {
                key: "cached",
                header: "Cached in / M",
                align: "end",
                render: (row) => <code>{row.cached_input_per_million}</code>,
              },
              {
                key: "cache_write",
                header: "Cache write / M",
                align: "end",
                render: (row) => <code>{row.cache_write_per_million}</code>,
              },
              {
                key: "output",
                header: "Output / M",
                align: "end",
                render: (row) => <code>{row.output_per_million}</code>,
              },
              {
                key: "reasoning",
                header: "Reasoning / M",
                align: "end",
                render: (row) => <code>{row.reasoning_per_million}</code>,
              },
              {
                key: "request_fee",
                header: "Per request",
                align: "end",
                render: (row) => <code>{row.request_fee}</code>,
              },
              {
                key: "status",
                header: "State",
                // The status column rather than a two-valued "in force /
                // superseded" badge: since a version can be RETIRED, a badge
                // that only knows two words would label a withdrawn price as
                // one that was replaced, which is the opposite of what the
                // gateway will do with the pair.
                render: (row) => <StatusBadge status={row.status} />,
              },
              {
                key: "effective",
                header: "Effective",
                render: (row) => <EffectiveRange from={row.effective_from} to={row.effective_to} />,
              },
              {
                key: "source",
                header: "Source",
                render: (row) => (
                  <>
                    <div>{row.source}</div>
                    <span className="esm-muted-text">
                      {row.verified_at ? `verified ${when(row.verified_at)}` : "never verified"}
                    </span>
                  </>
                ),
              },
              {
                key: "actions",
                header: "",
                align: "end",
                render: (row) => {
                  // Only the open version has a window left to close. A closed
                  // one is history — the API answers a second transition with
                  // a 412 naming what it was closed as — so there is no button
                  // to offer.
                  if (!writable || !row.in_force) {
                    return null;
                  }
                  const pair = `${row.provider}/${row.model} ${row.currency}`;
                  return (
                    <div className="esm-actions">
                      {PRICE_VERSION_ACTIONS.map((action) => (
                        <ReasonButton
                          key={action.status}
                          variant={action.danger ? "danger" : "secondary"}
                          busy={setStatus.isPending && setStatus.variables?.versionID === row.id}
                          mutation={{
                            title: `${action.label}: ${row.model}`,
                            tone: action.danger ? "danger" : "primary",
                            description: (
                              <p>
                                <code>{row.model}</code> on <code>{row.provider}</code>, priced in{" "}
                                {row.currency} at {row.input_per_million} in /{" "}
                                {row.output_per_million} out per million. {action.description}
                              </p>
                            ),
                            confirmLabel: action.label,
                            run: async (reason) => {
                              const result = await setStatus.mutateAsync({
                                versionID: row.id,
                                status: action.status,
                                reason,
                              });
                              onDone(
                                result.changed
                                  ? `${pair} is now ${result.status}.`
                                  : `${pair} was already ${result.status}; nothing moved.`,
                              );
                            },
                          }}
                        >
                          {action.label}
                        </ReasonButton>
                      ))}
                    </div>
                  );
                },
              },
            ]}
          />
        </>
      )}
      {writable ? <PriceVersionForm onDone={onDone} /> : null}
    </Card>
  );
}

const EMPTY_PRICE = {
  provider: "",
  model: "",
  currency: "USD",
  input_per_million: "",
  cached_input_per_million: "",
  cache_write_per_million: "",
  output_per_million: "",
  reasoning_per_million: "",
  request_fee: "",
  source: "",
};

function PriceVersionForm({ onDone }: { onDone: (message: string) => void }) {
  const create = useCreatePriceVersion();
  const [form, setForm] = useState(EMPTY_PRICE);
  const set = (field: keyof typeof EMPTY_PRICE) => (value: string) =>
    setForm((current) => ({ ...current, [field]: value }));

  const errors = {
    input: decimalError(form.input_per_million, true),
    output: decimalError(form.output_per_million, true),
    cached: decimalError(form.cached_input_per_million, false),
    cacheWrite: decimalError(form.cache_write_per_million, false),
    reasoning: decimalError(form.reasoning_per_million, false),
    requestFee: decimalError(form.request_fee, false),
  };
  const ready =
    form.provider.trim() !== "" &&
    form.model.trim() !== "" &&
    form.currency.trim() !== "" &&
    Object.values(errors).every((error) => error === undefined);

  const body = (): NewPriceVersion => ({
    provider: form.provider.trim(),
    model: form.model.trim(),
    currency: form.currency.trim(),
    // Verbatim, both here and in the client: these strings go to
    // internal/money exactly as typed. Nothing in this portal multiplies a
    // price by 100 or by a million on the way out.
    input_per_million: form.input_per_million.trim(),
    output_per_million: form.output_per_million.trim(),
    cached_input_per_million: optional(form.cached_input_per_million),
    cache_write_per_million: optional(form.cache_write_per_million),
    reasoning_per_million: optional(form.reasoning_per_million),
    request_fee: optional(form.request_fee),
    source: optional(form.source),
  });

  return (
    <div className="esm-subform">
      <h3>Publish a price version</h3>
      <p className="esm-muted-text">
        Publishing writes a NEW version, in force from now, and closes the one before it. It does
        not edit the existing prices: everything already costed keeps the rate it was costed at,
        which is why the table above is a history rather than a list of current numbers.
      </p>
      <ErrorAlert error={create.error} title="That price version was not published" />
      {/* Not a <form>: the write goes through the reason prompt rather than a
          submit, and an Enter key that posted straight past the prompt would
          be a mutation with no reason on it. */}
      <div className="esm-form-grid">
        <Field label="Provider" htmlFor="price-provider" hint="As the router names it.">
          <Input
            id="price-provider"
            value={form.provider}
            onChange={(event) => set("provider")(event.target.value)}
          />
        </Field>
        <Field label="Model" htmlFor="price-model">
          <Input
            id="price-model"
            value={form.model}
            onChange={(event) => set("model")(event.target.value)}
          />
        </Field>
        <Field label="Currency" htmlFor="price-currency">
          <Input
            id="price-currency"
            value={form.currency}
            onChange={(event) => set("currency")(event.target.value)}
          />
        </Field>
        <Field
          label="Input per million"
          htmlFor="price-input"
          hint="Major units, as billed: 3.00"
          error={errors.input}
        >
          <Input
            id="price-input"
            inputMode="decimal"
            value={form.input_per_million}
            onChange={(event) => set("input_per_million")(event.target.value)}
          />
        </Field>
        <Field label="Output per million" htmlFor="price-output" error={errors.output}>
          <Input
            id="price-output"
            inputMode="decimal"
            value={form.output_per_million}
            onChange={(event) => set("output_per_million")(event.target.value)}
          />
        </Field>
        <Field
          label="Cached input per million"
          htmlFor="price-cached"
          hint="Left empty means zero."
          error={errors.cached}
        >
          <Input
            id="price-cached"
            inputMode="decimal"
            value={form.cached_input_per_million}
            onChange={(event) => set("cached_input_per_million")(event.target.value)}
          />
        </Field>
        <Field label="Cache write per million" htmlFor="price-cache-write" error={errors.cacheWrite}>
          <Input
            id="price-cache-write"
            inputMode="decimal"
            value={form.cache_write_per_million}
            onChange={(event) => set("cache_write_per_million")(event.target.value)}
          />
        </Field>
        <Field label="Reasoning per million" htmlFor="price-reasoning" error={errors.reasoning}>
          <Input
            id="price-reasoning"
            inputMode="decimal"
            value={form.reasoning_per_million}
            onChange={(event) => set("reasoning_per_million")(event.target.value)}
          />
        </Field>
        <Field label="Request fee" htmlFor="price-request-fee" error={errors.requestFee}>
          <Input
            id="price-request-fee"
            inputMode="decimal"
            value={form.request_fee}
            onChange={(event) => set("request_fee")(event.target.value)}
          />
        </Field>
        <Field
          label="Source"
          htmlFor="price-source"
          hint="Where the numbers came from — a price page, a contract, an email."
        >
          <Input
            id="price-source"
            value={form.source}
            onChange={(event) => set("source")(event.target.value)}
          />
        </Field>
      </div>
      <div className="esm-actions">
        <ReasonButton
          variant="primary"
          busy={create.isPending}
          disabled={!ready}
          mutation={{
            title: `Publish a price for ${form.model.trim() || "this model"}`,
            description: (
              <p>
                A new version takes effect immediately and closes the current one. Requests already
                costed are not repriced, and the previous version stays readable in the history.
              </p>
            ),
            confirmLabel: "Publish price version",
            run: async (reason) => {
              const result = await create.mutateAsync({ body: body(), reason });
              setForm(EMPTY_PRICE);
              onDone(
                `Published ${result.price_version.provider} ${result.price_version.model} at ${result.price_version.input_per_million} in / ${result.price_version.output_per_million} out per million ${result.price_version.currency}.`,
              );
            },
          }}
        >
          Publish price version
        </ReasonButton>
      </div>
    </div>
  );
}

// --------------------------------------------------------------------------
// Markup plans
// --------------------------------------------------------------------------

/** What the markup plan form is for: a new plan, or a correction to one that
 *  is in force. */
type PlanTarget = { mode: "create" } | { mode: "edit"; row: MarkupPlan };

/**
 * The routing profiles named by an archive refusal.
 *
 * Read off the problem's EXTENSION MEMBERS rather than out of its sentence.
 * The sentence names them by slug and is shown as well, but the members carry
 * the ids, which is the only thing that turns "these three profiles are in the
 * way" into a link an operator can follow to move them — and the operator's
 * next move here is exactly that.
 *
 * `problem` declares only the RFC 9457 reserved members, so reading an
 * extension needs the cast; every field is checked before it is trusted,
 * because this is a response body and not a local value.
 */
function blockingProfiles(error: unknown): RoutingProfileRef[] | undefined {
  if (!isApiError(error) || error.code !== "conflict" || !error.problem) {
    return undefined;
  }
  const members = error.problem as unknown as Record<string, unknown>;
  const raw = members["routing_profiles"];
  if (!Array.isArray(raw)) {
    return undefined;
  }
  const refs: RoutingProfileRef[] = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== "object") {
      return undefined;
    }
    const ref = entry as Record<string, unknown>;
    if (
      typeof ref["id"] !== "string" ||
      typeof ref["slug"] !== "string" ||
      typeof ref["name"] !== "string" ||
      typeof ref["status"] !== "string"
    ) {
      return undefined;
    }
    refs.push({
      id: ref["id"],
      slug: ref["slug"],
      name: ref["name"],
      status: ref["status"],
    });
  }
  return refs.length > 0 ? refs : undefined;
}

/**
 * An archive refusal, with the profiles that caused it.
 *
 * The API's sentence is rendered either way — it is the part that explains the
 * rule — and the list is added under it when the refusal named one. The
 * placement is computed BEFORE the plain alert is given up: a branch that
 * decided "this is the in-use conflict" and then failed to find any profiles
 * would swallow the refusal entirely.
 */
function ArchiveRefusal({ error }: { error: unknown }) {
  if (!error) {
    return null;
  }
  const profiles = blockingProfiles(error);
  if (!profiles) {
    return <ErrorAlert error={error} title="That markup plan was not archived" />;
  }
  return (
    <Alert tone="danger" title="That markup plan was not archived">
      <p>{errorMessage(error)}</p>
      <ul className="esm-ref-list">
        {profiles.map((profile) => (
          <li key={profile.id}>
            <Link to="/routing-profiles/$profileId" params={{ profileId: profile.id }}>
              {profile.name}
            </Link>{" "}
            <code className="esm-muted-text">{profile.slug}</code>{" "}
            <StatusBadge status={profile.status} />
          </li>
        ))}
      </ul>
    </Alert>
  );
}

function MarkupPlansCard({
  writable,
  onDone,
}: {
  writable: boolean;
  onDone: (message: string | null) => void;
}) {
  const plans = useMarkupPlans({ limit: PAGE });
  const archive = useArchiveMarkupPlan();
  const [target, setTarget] = useState<PlanTarget | null>(null);

  return (
    <Card title="Markup plans">
      {plans.isPending ? (
        <Spinner label="Loading markup plans" />
      ) : plans.error ? (
        <ErrorAlert error={plans.error} title="Could not load the markup plans" />
      ) : (
        <>
          <ArchiveRefusal error={archive.error} />
          <DataTable<MarkupPlan>
            rows={plans.data?.markup_plans ?? []}
            rowKey={(row) => row.id}
            empty="No markup plan has been published, so nothing has a platform price."
            columns={[
              {
                key: "plan",
                header: "Plan",
                render: (row) => (
                  <>
                    <div>{row.name}</div>
                    <code className="esm-muted-text">{row.slug}</code>
                  </>
                ),
              },
              {
                key: "scope",
                header: "Applies to",
                render: (row) =>
                  row.is_platform_default ? (
                    <Badge tone="warning">platform default</Badge>
                  ) : (
                    row.application_name ?? <code>{row.application_id}</code>
                  ),
              },
              { key: "currency", header: "Currency", render: (row) => row.currency },
              {
                key: "percentage",
                header: "Markup",
                align: "end",
                // Basis points are what the server stores and what an operator
                // typed; the percentage beside it is the sanity check that 1500
                // was not meant to be 15.
                render: (row) => (
                  <>
                    <div>{(row.percentage_bps / 100).toFixed(2)}%</div>
                    <span className="esm-muted-text">{row.percentage_bps} bps</span>
                  </>
                ),
              },
              {
                key: "fees",
                header: "Request fee / minimum",
                align: "end",
                render: (row) => (
                  <code>
                    {row.fixed_request_fee} / {row.minimum_charge}
                  </code>
                ),
              },
              {
                key: "rounding",
                header: "Rounding",
                render: (row) => <code>{row.rounding_mode}</code>,
              },
              {
                key: "status",
                header: "State",
                render: (row) => <StatusBadge status={row.status} />,
              },
              {
                key: "effective",
                header: "Effective",
                render: (row) => <EffectiveRange from={row.effective_from} to={row.effective_to} />,
              },
              {
                key: "actions",
                header: "",
                align: "end",
                render: (row) => {
                  // A closed version is history: editing one is a 412 naming
                  // what it was closed as, and archiving it would close it
                  // twice. The row in force is the only one with anything to
                  // change.
                  if (!writable || !row.in_force) {
                    return null;
                  }
                  return (
                    <div className="esm-actions">
                      {/* Opening the form is not a write, so it is a plain
                          button. The reason prompt sits on Save, where the
                          request actually is. */}
                      <Button
                        variant="ghost"
                        onClick={() => {
                          onDone(null);
                          setTarget({ mode: "edit", row });
                        }}
                      >
                        Edit
                      </Button>
                      {row.is_platform_default ? (
                        // Not a disabled button: the API refuses this with a
                        // 412 and the reason is worth reading, whereas a dead
                        // control explains nothing. Every application without
                        // a plan of its own is priced under this one, so
                        // closing it would leave those requests with no plan
                        // in force at all.
                        <span className="esm-muted-text">
                          never archived — every application without its own plan is priced under
                          it
                        </span>
                      ) : (
                        <ReasonButton
                          variant="danger"
                          busy={archive.isPending && archive.variables?.planID === row.id}
                          mutation={{
                            title: `Archive ${row.name}`,
                            description: (
                              <p>
                                <code>{row.slug}</code> stops being applied from now.{" "}
                                {row.application_name ?? "The application"} keeps everything already
                                charged under it — this closes the window, it does not restate a
                                bill. It is refused while an active routing profile still prices
                                against this plan.
                              </p>
                            ),
                            confirmLabel: "Archive plan",
                            run: async (reason) => {
                              const result = await archive.mutateAsync({
                                planID: row.id,
                                reason,
                              });
                              onDone(
                                result.changed
                                  ? `${row.name} is now ${result.status}.`
                                  : `${row.name} was already ${result.status}; nothing moved.`,
                              );
                            },
                          }}
                        >
                          Archive
                        </ReasonButton>
                      )}
                    </div>
                  );
                },
              },
            ]}
          />
        </>
      )}
      {/* Keyed by what the form is for, so opening Edit on a second row
          rebuilds the state rather than leaving the first row's values in the
          boxes under a new heading. Create is the resting state: publishing a
          plan is what this card is for, and Cancel on an edit comes back to
          it. */}
      {writable ? (
        <MarkupPlanForm
          key={target?.mode === "edit" ? target.row.id : "create"}
          target={target ?? { mode: "create" }}
          onClose={() => setTarget(null)}
          onDone={(message) => {
            setTarget(null);
            onDone(message);
          }}
        />
      ) : null}
    </Card>
  );
}

interface PlanFormState {
  application_id: string;
  slug: string;
  name: string;
  currency: string;
  percentage_bps: string;
  fixed_request_fee: string;
  minimum_charge: string;
  rounding_mode: RoundingMode;
}

function initialPlanForm(target: PlanTarget): PlanFormState {
  if (target.mode === "create") {
    return {
      application_id: "",
      slug: "",
      name: "",
      currency: "USD",
      percentage_bps: "",
      fixed_request_fee: "",
      minimum_charge: "",
      rounding_mode: "half-up",
    };
  }
  const row = target.row;
  return {
    // Empty is how the platform default's scope is spelled on the wire, and
    // it is what the server compares an echoed application_id against.
    application_id: row.application_id ?? "",
    slug: row.slug,
    name: row.name,
    currency: row.currency,
    percentage_bps: String(row.percentage_bps),
    // Verbatim: these are the decimal strings the server formatted from the
    // integer micros it stores, so an untouched save round-trips to the same
    // integer. Anything that parsed them into a Number first would not.
    fixed_request_fee: row.fixed_request_fee,
    minimum_charge: row.minimum_charge,
    rounding_mode: row.rounding_mode,
  };
}

/**
 * The one form behind "Publish a markup plan" and every in-force row's "Edit".
 *
 * One component rather than two, for the reason the model form gives: the
 * fields are the same fields, and two copies would drift the first time a
 * column is added — the create half would grow it and the edit half would
 * quietly stop round tripping it, which reads to an operator as the console
 * clearing a value they never touched.
 *
 * The edit half PATCHes the WHOLE plan back, scope included. Echoing an
 * application, a slug or a currency unchanged is a no-op; moving one is a 409
 * naming which. Sending everything also means a save that changes nothing is a
 * 200 rather than the "this edit changes nothing" 400 an empty PATCH earns.
 */
function MarkupPlanForm({
  target,
  onClose,
  onDone,
}: {
  target: PlanTarget;
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const create = useCreateMarkupPlan();
  const update = useUpdateMarkupPlan();
  const [form, setForm] = useState<PlanFormState>(() => initialPlanForm(target));
  const creating = target.mode === "create";

  const bps = Number.parseInt(form.percentage_bps.trim(), 10);
  const bpsError =
    form.percentage_bps.trim() === ""
      ? "Required."
      : !/^\d+$/.test(form.percentage_bps.trim())
        ? "Whole basis points — 1500 is 15%."
        : undefined;
  const feeError = decimalError(form.fixed_request_fee, false);
  const minimumError = decimalError(form.minimum_charge, false);
  const ready =
    form.slug.trim() !== "" &&
    form.name.trim() !== "" &&
    form.currency.trim() !== "" &&
    bpsError === undefined &&
    feeError === undefined &&
    minimumError === undefined;

  const label = creating ? "Publish markup plan" : "Save as a new version";
  const name = form.name.trim() || "this plan";

  async function run(reason: Reason) {
    if (creating) {
      const body: NewMarkupPlan = {
        application_id: optional(form.application_id),
        slug: form.slug.trim(),
        name: form.name.trim(),
        currency: form.currency.trim(),
        percentage_bps: bps,
        fixed_request_fee: optional(form.fixed_request_fee),
        minimum_charge: optional(form.minimum_charge),
        rounding_mode: form.rounding_mode,
      };
      const result = await create.mutateAsync({ body, reason });
      onDone(
        `Published ${result.markup_plan.name} at ${result.markup_plan.percentage_bps} bps in ${result.markup_plan.currency}.`,
      );
      return;
    }
    const body: MarkupPlanEdit = {
      // Echoed from the ROW, never from an input: the three are the plan's
      // scope, there is no box that could move them, and echoing is what
      // makes this a whole-row save rather than a partial one.
      application_id: target.row.application_id ?? "",
      slug: target.row.slug,
      currency: target.row.currency,
      name: form.name.trim(),
      percentage_bps: bps,
      // Sent whatever they are: the boxes were seeded from the row, so an
      // untouched save re-sends the same rates, and an emptied box is the
      // server's "" — which it reads as zero, the only thing "no fee" can
      // mean on a plan that must always price something.
      fixed_request_fee: form.fixed_request_fee.trim(),
      minimum_charge: form.minimum_charge.trim(),
      rounding_mode: form.rounding_mode,
    };
    const result = await update.mutateAsync({ planID: target.row.id, body, reason });
    const repointed = result.repointed_routing_profiles;
    onDone(
      `${result.markup_plan.name} is now at ${result.markup_plan.percentage_bps} bps; the previous version is closed.` +
        (repointed.length > 0
          ? ` Moved ${repointed.length} routing profile(s) onto it: ${repointed
              .map((profile) => profile.slug)
              .join(", ")}.`
          : ""),
    );
  }

  return (
    <div className="esm-subform">
      <h3>{creating ? "Publish a markup plan" : `Edit ${target.row.name}`}</h3>
      <p className="esm-muted-text">
        {creating
          ? "Publishing writes a NEW plan, in force from now, and closes the previous one for the same application and currency. It does not edit the plan in place — anything already charged stays charged under the plan that was in force at the time."
          : "Saving APPENDS a new version of this plan and closes the current one at the same instant. The row above is not rewritten: it stays readable as what was charged until now, because wallet reservations and usage records pin the version they settled under. Every routing profile pinned to the current version is moved onto the successor in the same transaction."}
      </p>
      {/* Whatever the API said, verbatim. A 409 here names which half of the
          plan's scope was aimed at, and a house failure banner would throw
          away the only part of the answer that says what to do next. */}
      <ErrorAlert
        error={creating ? create.error : update.error}
        title={creating ? "That markup plan was not published" : "That change was not saved"}
      />
      {/* Not a <form>: the write goes through the reason prompt rather than a
          submit, and an Enter key that posted straight past the prompt would
          be a mutation with no reason on it. */}
      <div className="esm-form-grid">
        <Field
          label="Application"
          htmlFor="plan-application"
          hint={
            creating
              ? "An application id, or empty for the platform default plan."
              : "Fixed: an edit appends into the same scope."
          }
        >
          <Input
            id="plan-application"
            value={
              creating
                ? form.application_id
                : (target.row.application_name ?? (form.application_id || "platform default"))
            }
            readOnly={!creating}
            onChange={(event) =>
              setForm((current) => ({ ...current, application_id: event.target.value }))
            }
          />
        </Field>
        <Field label="Slug" htmlFor="plan-slug" hint={creating ? undefined : "Fixed."}>
          <Input
            id="plan-slug"
            value={form.slug}
            readOnly={!creating}
            onChange={(event) => setForm((current) => ({ ...current, slug: event.target.value }))}
          />
        </Field>
        <Field label="Name" htmlFor="plan-name">
          <Input
            id="plan-name"
            value={form.name}
            onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
          />
        </Field>
        <Field label="Currency" htmlFor="plan-currency" hint={creating ? undefined : "Fixed."}>
          <Input
            id="plan-currency"
            value={form.currency}
            readOnly={!creating}
            onChange={(event) =>
              setForm((current) => ({ ...current, currency: event.target.value }))
            }
          />
        </Field>
        <Field
          label="Markup (basis points)"
          htmlFor="plan-bps"
          hint="1500 is 15%."
          error={bpsError}
        >
          <Input
            id="plan-bps"
            inputMode="numeric"
            value={form.percentage_bps}
            onChange={(event) =>
              setForm((current) => ({ ...current, percentage_bps: event.target.value }))
            }
          />
        </Field>
        <Field
          label="Fixed request fee"
          htmlFor="plan-fee"
          hint="Major units. Left empty means zero."
          error={feeError}
        >
          <Input
            id="plan-fee"
            inputMode="decimal"
            value={form.fixed_request_fee}
            onChange={(event) =>
              setForm((current) => ({ ...current, fixed_request_fee: event.target.value }))
            }
          />
        </Field>
        <Field
          label="Minimum charge"
          htmlFor="plan-minimum"
          hint="The floor for any single request."
          error={minimumError}
        >
          <Input
            id="plan-minimum"
            inputMode="decimal"
            value={form.minimum_charge}
            onChange={(event) =>
              setForm((current) => ({ ...current, minimum_charge: event.target.value }))
            }
          />
        </Field>
        <Field
          label="Rounding"
          htmlFor="plan-rounding"
          hint="How a fractional charge is resolved."
        >
          <Select
            id="plan-rounding"
            value={form.rounding_mode}
            onChange={(event) =>
              setForm((current) => ({
                ...current,
                // The Select can only offer the six modes the wire accepts,
                // so the cast is over a closed set rather than over input.
                rounding_mode: event.target.value as RoundingMode,
              }))
            }
          >
            {ROUNDING_MODES.map((mode) => (
              <option key={mode} value={mode}>
                {mode}
              </option>
            ))}
          </Select>
        </Field>
      </div>
      <div className="esm-actions">
        <ReasonButton
          variant="primary"
          busy={create.isPending || update.isPending}
          disabled={!ready}
          mutation={{
            title: creating ? `Publish ${name}` : `Save ${name} as a new version`,
            tone: "primary",
            description: creating ? (
              <p>
                {form.application_id.trim() === ""
                  ? "This becomes the platform default plan, applying wherever an application has no plan of its own."
                  : "This becomes the plan for that application."}{" "}
                It closes the previous plan for the same scope from now; nothing already charged is
                recalculated.
              </p>
            ) : (
              <p>
                This does not edit <code>{target.row.slug}</code> in place: it writes a NEW version
                at {form.percentage_bps.trim() || "0"} bps, in force from now, and closes the
                current one at the same instant. Nothing already charged is recalculated, and any
                routing profile pinned to the current version is moved onto the new one with it.
              </p>
            ),
            confirmLabel: label,
            run,
          }}
        >
          {label}
        </ReasonButton>
        {creating ? null : (
          <Button variant="ghost" disabled={update.isPending} onClick={onClose}>
            Cancel
          </Button>
        )}
      </div>
    </div>
  );
}
